<script setup>
import { computed, ref } from 'vue';

import { fieldEmits, fieldProps, useField } from '@/composables/form/field';
import { collectionV2 } from '@/composables/resource';

import FieldContainer from './FieldContainer.vue';
import FieldWrapper from './FieldWrapper.vue';

const emit = defineEmits(fieldEmits(['fetched:options']));

const props = defineProps(fieldProps({
    options: {
        type: [Object, String],
        default: () => {},
    },
    compact: {
        required: false,
        type: Boolean,
        default: false,
    }
}));

const element = ref();

const set = (state) => {
    if (apiOptions) {
        apiOptions.fetchOnce().then(() => {
            element.value.value = state;
        });
    } else {
        element.value.value = state;
    }
};

const castStateUsing = (state) => String(state ?? '');

const field = useField({ props, emit, set, castStateUsing });

const apiOptions = typeof props.options === 'string' ? collectionV2({
    url: props.options
}) : null;

const shouldShowPlaceholder = computed(() => {
    return typeof field.props.placeholder !== 'undefined';
});

const getOptionsArray = computed(() => {
    let options = [];

    if (apiOptions) {
        apiOptions.data.forEach(option => {
            options.push({
                label: option.name,
                value: option.id,
            });

            const children = option.children ?? [];
            children.forEach(child => {
                options.push({
                    parent: option.id,
                    label: child.name,
                    value: child.id,
                });
            });
        });
    } else {
        const keys = Object.keys(props.options ?? {});

        options = keys.map(key => ({
            label: props.options[key],
            value: key,
        }));
    }

    if (shouldShowPlaceholder.value) {
        options.unshift({
            label: field.props.placeholder,
            value: '',
            placeholder: true,
        });
    }

    return options;
});
</script>

<template>
    <FieldContainer>
        <FieldWrapper>
            <select
                :id="field.id"
                :name="field.name"
                :disabled="field.disabled"
                :aria-required="field.required"
                :aria-label="field.label && field.hideLabel ? field.label : undefined"
                ref="element"
                class="
                block w-full focus:outline-none
                focus:ring-0 border-0 bg-white text-white-foreground p-0 disabled:opacity-50"
                :class="{
                    'text-white-foreground text-opacity-30': !field.state,
                }"
                @change="field.updateState({ value: $event.target.value ?? null, event: $event })"
            >
                <option
                    v-for="option in getOptionsArray"
                    :key="option.value"
                    :value="option.value"
                >
                    {{ option.parent ? '➖ ' + option.label : option.label }}
                </option>
            </select>
        </FieldWrapper>
    </FieldContainer>
</template>
